import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { FolderService } from "@/services/FolderService";
import { APIHosts } from "@/services/reduxHelpers";
import { ACCESS_KEY, getBootstrapConfig } from "@/api/HttpClient";

let workflowConnection: HubConnection | null = null;
let workflowConnectionPromise: Promise<HubConnection | null> | null = null;
const EVENT_DEDUPE_WINDOW_MS = 3000;
const recentEventTimestamps = new Map<string, number>();

// Thông tin log đẩy về từ RabbitMQ hub
export type RabbitMqLog = {
  id?: string;
  workflowId?: string;
  workflow_id?: string;
  documentId?: string;
  document_id?: string;
  baseFileId?: string;
  base_file_id?: string;
  fileId?: string;
  file_id?: string;
  taskId?: string;
  task_id?: string;
  taskName?: string;
  task_name?: string;
  stage?: string;
  status?: string;
  event_type?: string;
  category?: string;
  queue?: string;
  timestamp?: string;
  monitoring_metadata?: {
    processed_at?: string;
    source?: string;
    version?: string;
  };
  signalr_metadata?: {
    category?: string;
    sent_at?: string;
    source?: string;
  };
  _id?: string;
  _workflow_group?: string;
  [key: string]: unknown;
  duration?: number;
};

// Dữ liệu chuẩn hoá cho UI sử dụng
export type WorkflowStatusUpdate = {
  workflow_id: string;
  status: string;
  raw: RabbitMqLog;
  workflowName: string;
};

export type ConnectionState =
  | "Connecting"
  | "Connected"
  | "Reconnecting"
  | "Disconnected"
  | "Failed";

export const getWorkflowConnection = () => workflowConnection;

export const startWorkflowConnection = async (
  onStatusUpdate: (update: RabbitMqLog) => void,
  onConnectionStateChange?: (state: ConnectionState) => void,
): Promise<HubConnection | null> => {
  if (workflowConnection) {
    return workflowConnection;
  }
  if (workflowConnectionPromise) {
    return workflowConnectionPromise;
  }
  const connectPromise = (async (): Promise<HubConnection | null> => {
  const baseUrl = FolderService.getHost(APIHosts.Workflow);

  if (!baseUrl) {
    console.warn(
      "Workflow host (hostworkflow) is not configured. Realtime workflow updates disabled.",
    );
    return null;
  }

  const accessToken = localStorage.getItem(ACCESS_KEY) ?? undefined;

  const cfg = getBootstrapConfig();
  const configuredHubPath = (cfg?.workflowHubPath as string) || undefined;
  // Tự động dò đường dẫn hub thực tế từ cấu hình / backend
  const hubPath = await discoverHubPath(baseUrl, configuredHubPath, accessToken);
  const fullHubUrl = normalizeUrl(baseUrl, hubPath);
  console.info("SignalR workflow hub url:", fullHubUrl);

  const connection = new HubConnectionBuilder()
    .withUrl(fullHubUrl, {
      withCredentials: false,
      accessTokenFactory: () => accessToken ?? "",
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Information)
    .build();
    
  console.info("SignalR workflow: connecting to", fullHubUrl);

  const emitUpdateFromLog = (source: string, logData: unknown) => {
    try {
      const raw: RabbitMqLog =
        typeof logData === "string"
          ? JSON.parse(logData)
          : (logData as RabbitMqLog);

      if (!raw || typeof raw !== "object") return;

      const workflowId =
        String(
          raw.workflow_id ??
          raw.workflowId ??
          raw.document_id ??
          raw.documentId ??
          raw.file_id ??
          raw.fileId ??
          "",
        );
      const taskId = String(raw.task_id ?? raw.taskId ?? "");
      const eventType = String(raw.event_type ?? raw.status ?? source);
      const timestamp = String(raw.timestamp ?? "");
      const dedupeKey = `${workflowId}|${taskId}|${eventType}|${timestamp}`;
      const now = Date.now();
      const lastSeen = recentEventTimestamps.get(dedupeKey);
      if (lastSeen && now - lastSeen < EVENT_DEDUPE_WINDOW_MS) {
        return;
      }

      recentEventTimestamps.set(dedupeKey, now);
      if (recentEventTimestamps.size > 2000) {
        for (const [key, seenAt] of recentEventTimestamps.entries()) {
          if (now - seenAt > EVENT_DEDUPE_WINDOW_MS * 2) recentEventTimestamps.delete(key);
        }
      }

      onStatusUpdate(raw);
    } catch (e) {
      console.error(`Error processing ${source} message`, e);
    }
  };

  // const emitUpdateFromLog = (source: string, logData: any) => {
  //   try {
  //     console.debug(`SignalR ${source} raw:`, logData);
  //     const raw: RabbitMqLog =
  //       typeof logData === "string" ? JSON.parse(logData) : logData;

  //     if (!raw) return;

  //     // Hỗ trợ cả camelCase và snake_case từ backend
  //     const idCandidate =
  //       (raw as any).documentId ??
  //       (raw as any).document_id ??
  //       (raw as any).baseFileId ??
  //       (raw as any).base_file_id ??
  //       (raw as any).fileId ??
  //       (raw as any).file_id ??
  //       (raw as any).workflowId ??
  //       (raw as any).workflow_id ??
  //       (raw as any).id;

  //     const workflow_id = idCandidate ? String(idCandidate) : "";

  //     const statusCandidate = (raw as any).status ?? (raw as any).event_type;
  //     const workflowName = (raw as any)?.workflow_name ?? (raw as any)?.workflow_name;
  //     const status =
  //       String(
  //         statusCandidate ||
  //           (source === "taskcompleted" || source === "workflowcompleted"
  //             ? "Completed"
  //             : source === "taskstarted" || source === "workflowstarted"
  //               ? "Started"
  //               : source === "workflowcreatedfromtemplate"
  //                 ? "Created"
  //                 : ""),
  //       ) || "";
  //     onStatusUpdate({ workflow_id, status, raw, workflowName });
  //   } catch (e) {
  //     console.error(`Error processing ${source} message`, e);
  //   }
  // };

  // Nhận log từ hub và chuẩn hoá sang WorkflowStatusUpdate
  connection.on("workflow", (logData: unknown) => {
    emitUpdateFromLog("RabbitMqLogReceived", logData);
  });

  // Một số backend gửi log qua các method khác như taskcompleted / taskstarted / workflowcompleted
  // connection.on("workflowcompleted", (...args: any[]) => {
  //   console.debug("SignalR workflowcompleted args:", args);
  //   const payload = args.length === 1 ? args[0] : { args };
  //   emitUpdateFromLog("workflowcompleted", payload);
  // });

  // connection.on("workflowcreatedfromtemplate", (...args: any[]) => {
  //   console.debug("SignalR workflowcreatedfromtemplate args:", args);
  //   const payload = args.length === 1 ? args[0] : { args };
  //   emitUpdateFromLog("workflowcreatedfromtemplate", payload);
  // });

  connection.on("eventmonitoring", (...args: unknown[]) => {
    const payload = args.length === 1 ? args[0] : { args };
    emitUpdateFromLog("workflowstarted", payload);
  });

  // Một số backend có thể gọi method "connected" trên client khi kết nối thành công
  // Nếu không khai báo handler, SignalR sẽ cảnh báo: "No client method with the name 'connected' found."
  connection.on("connected", (...args: unknown[]) => {
    console.info("SignalR connected event received", args);
    // Hiện tại chỉ log lại để tránh warning; có thể tuỳ biến thêm nếu backend gửi thêm payload.
  });

  // Theo dõi trạng thái kết nối
  connection.onreconnecting(() => {
    onConnectionStateChange?.("Reconnecting");
  });

  connection.onreconnected(() => {
    onConnectionStateChange?.("Connected");
  });

  connection.onclose(() => {
    onConnectionStateChange?.("Disconnected");
  });

  try {
    onConnectionStateChange?.("Connecting");
    await connection.start();
    console.info("SignalR workflow connection started");
    workflowConnection = connection;
    onConnectionStateChange?.("Connected");
    return connection;
  } catch (err) {
    console.error("Failed to start SignalR workflow connection", {
      error: err,
      baseUrl,
      hubPath,
      fullHubUrl,
    });
    onConnectionStateChange?.("Failed");
    return null;
  }
  })();

  workflowConnectionPromise = connectPromise;
  try {
    return await connectPromise;
  } finally {
    workflowConnectionPromise = null;
  }
};

function normalizeUrl(base: string, path: string): string {
  const b = base.endsWith("/") ? base.slice(0, -1) : base;
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
}

async function discoverHubPath(
  baseUrl: string,
  configuredPath: string | undefined,
  token: string | undefined,
): Promise<string> {
  const candidates = configuredPath
    ? [
        configuredPath,
        "/hubs/workflow",
      ]
    : [
        "/hubs/workflow",
      ];

  for (const p of candidates) {
    const negotiateUrl = `${normalizeUrl(baseUrl, p)}/negotiate?negotiateVersion=1`;
    try {
      const res = await fetch(negotiateUrl, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
        },
        credentials: "omit",
      });
      if (res.ok) {
        return p;
      }
    } catch {
      // ignore
    }
  }

  // fallback: original default
  return configuredPath || "/hubs/workflow";
}

export const stopWorkflowConnection = async () => {
  workflowConnectionPromise = null;
  if (workflowConnection) {
    try {
      await workflowConnection.stop();
    } catch (e) {
      console.error("Error stopping SignalR workflow connection", e);
    } finally {
      workflowConnection = null;
    }
  }
};
